/**
 * EpisodeSelectionService.js - Centralized episode selection logic
 *
 * Handles episode selection based on resumption data with the following criteria:
 * 1. If the latest (via updatedAt) resumption data is in watch again, the next episode must be selected
 * 2. If the latest resumption is in continue watching, that episode must be selected
 * 3. If there is no resumptions data for episodes in that channel, start on the first episode
 */

import userDataService from './UserDataService';

/**
 * @typedef {Object} EpisodeSelectionResult
 * @property {Object|null} episode - The selected episode object
 * @property {Object|null} season - The season containing the episode (if different from current)
 * @property {string} selectionReason - Reason for the selection ('continue-watching', 'watch-again-next', 'first-episode', 'no-data')
 * @property {Object|null} resumptionData - The resumption data that led to this selection
 */

class EpisodeSelectionService {
  /**
   * Select the appropriate episode for a series based on resumption data
   * @param {string} seriesId - The series ID to find episodes for
   * @param {Array} availableEpisodes - Array of episodes available in the current season
   * @param {Array} availableSeasons - Array of all seasons for the series
   * @param {Object} currentSeason - The currently selected season
   * @returns {EpisodeSelectionResult} The selection result with episode and metadata
   */
  selectEpisodeForSeries(
    seriesId,
    availableEpisodes = [],
    availableSeasons = [],
    currentSeason = null,
    initialEpisodeId = null,
  ) {
    try {
      // Priority 1: If initialEpisodeId is provided, use it (user explicitly selected an episode)
      if (initialEpisodeId) {
        // Find the episode in available episodes
        const targetEpisode = availableEpisodes.find(episode => episode._id === initialEpisodeId);

        if (targetEpisode) {
          return {
            episode: targetEpisode,
            season: null, // Stay in current season
            selectionReason: 'user-selection',
            resumptionData: null,
          };
        } else {
          // Episode not in current season — scan other seasons for one whose
          // playlist actually contains the target episode. Don't just pick "any
          // non-current season"; for 3+-season series that returned the wrong
          // season (e.g. picking S2 when the episode lives in S4) and could
          // ping-pong between seasons on re-runs.
          for (const season of availableSeasons) {
            if (season._id === currentSeason?._id) {continue;}
            const playlist = season.playlist || season.episodes || [];
            if (Array.isArray(playlist) && playlist.some(ep => ep?._id === initialEpisodeId)) {
              return {
                episode: null, // Will be selected when season changes
                season: season,
                selectionReason: 'user-selection',
                resumptionData: null,
              };
            }
          }
          // No season contained the episode — fall through to resumption/first-episode logic.
        }
      }

      // Priority 2: Use resumption data if no initialEpisodeId or initialEpisodeId not found
      const latestResumption = userDataService.getLatestResumption(seriesId);

      if (!latestResumption) {
        return this.selectFirstEpisode(availableEpisodes);
      }

      // Apply selection criteria based on resumption type
      if (latestResumption.type === 'watch-again') {
        return this.selectNextEpisodeAfterWatchAgain(
          latestResumption,
          availableEpisodes,
          availableSeasons,
          currentSeason,
        );
      } else if (latestResumption.type === 'continue-watching') {
        return this.selectContinueWatchingEpisode(
          latestResumption,
          availableEpisodes,
          availableSeasons,
          currentSeason,
        );
      } else {
        return this.selectFirstEpisode(availableEpisodes);
      }
    } catch (error) {
      console.error('🎬 EpisodeSelectionService: Error in episode selection:', error);
      return this.selectFirstEpisode(availableEpisodes);
    }
  }

  /**
   * Get all resumption data for a specific series (for backward compatibility)
   * @param {string} seriesId - The series ID
   * @returns {Object|null} Object with continue-watching and watch-again arrays, or null if no data
   */
  getResumptionDataForSeries(seriesId) {
    // Use the new method from UserDataService for better performance
    return userDataService.getResumptionDataForSeries(seriesId);
  }

  /**
   * Select the next episode after a watch-again episode
   * @param {Object} watchAgainResumption - The watch-again resumption data
   * @param {Array} availableEpisodes - Available episodes in current season
   * @param {Array} availableSeasons - All available seasons
   * @param {Object} currentSeason - Current season
   * @returns {EpisodeSelectionResult} Selection result
   */
  selectNextEpisodeAfterWatchAgain(
    watchAgainResumption,
    availableEpisodes,
    availableSeasons,
    currentSeason,
  ) {
    const watchAgainEpisode = watchAgainResumption.episode;

    // Check if there are more episodes available after this watch-again episode
    const hasMoreEpisodes = this.hasMoreEpisodesAfter(watchAgainEpisode, availableSeasons);

    // If there are no more episodes, this means the series is truly completed
    // In this case, we should select the first episode (for "Watch Again" behavior)
    if (!hasMoreEpisodes) {
      return this.selectFirstEpisode(availableEpisodes);
    }

    // Find the watch-again episode in the available episodes
    const episodeIndex = availableEpisodes.findIndex(ep => ep._id === watchAgainEpisode._id);

    if (episodeIndex === -1) {
      // Episode not in current season, need to find which season it's in
      const targetSeason = this.findSeasonForEpisode(watchAgainEpisode, availableSeasons);
      if (targetSeason) {
        // Watch-again episode is in different season
        return {
          episode: null,
          season: targetSeason,
          selectionReason: 'watch-again-next',
          resumptionData: watchAgainResumption,
        };
      } else {
        // Could not find season for watch-again episode, selecting first episode
        return this.selectFirstEpisode(availableEpisodes);
      }
    }

    // Find the next episode in the same season
    const nextEpisodeIndex = episodeIndex + 1;
    if (nextEpisodeIndex < availableEpisodes.length) {
      const nextEpisode = availableEpisodes[nextEpisodeIndex];
      // Found next episode in same season
      return {
        episode: nextEpisode,
        season: null,
        selectionReason: 'watch-again-next',
        resumptionData: watchAgainResumption,
      };
    } else {
      // No next episode in current season, check if there's a next season
      const currentSeasonIndex = availableSeasons.findIndex(
        season => season._id === currentSeason?._id,
      );
      if (currentSeasonIndex !== -1 && currentSeasonIndex < availableSeasons.length - 1) {
        const nextSeason = availableSeasons[currentSeasonIndex + 1];
        // Watch-again episode was last in season, moving to next season
        return {
          episode: null, // Will be first episode of next season
          season: nextSeason,
          selectionReason: 'watch-again-next',
          resumptionData: watchAgainResumption,
        };
      } else {
        return this.selectFirstEpisode(availableEpisodes);
      }
    }
  }

  /**
   * Select the continue-watching episode
   * @param {Object} continueWatchingResumption - The continue-watching resumption data
   * @param {Array} availableEpisodes - Available episodes in current season
   * @param {Array} availableSeasons - All available seasons
   * @param {Object} currentSeason - Current season
   * @returns {EpisodeSelectionResult} Selection result
   */
  selectContinueWatchingEpisode(
    continueWatchingResumption,
    availableEpisodes,
    availableSeasons,
    currentSeason,
  ) {
    const continueWatchingEpisode = continueWatchingResumption.episode;

    // Try to find the episode in the current season
    let targetEpisode = availableEpisodes.find(ep => ep._id === continueWatchingEpisode._id);

    if (!targetEpisode) {
      // Episode not in current season, need to find which season it's in
      const targetSeason = this.findSeasonForEpisode(continueWatchingEpisode, availableSeasons);
      if (targetSeason) {
        return {
          episode: continueWatchingEpisode,
          season: targetSeason,
          selectionReason: 'continue-watching',
          resumptionData: continueWatchingResumption,
        };
      } else {
        return this.selectFirstEpisode(availableEpisodes);
      }
    }

    return {
      episode: targetEpisode,
      season: null,
      selectionReason: 'continue-watching',
      resumptionData: continueWatchingResumption,
    };
  }

  /**
   * Select the first episode as fallback
   * @param {Array} availableEpisodes - Available episodes
   * @returns {EpisodeSelectionResult} Selection result
   */
  selectFirstEpisode(availableEpisodes) {
    const firstEpisode = availableEpisodes.length > 0 ? availableEpisodes[0] : null;

    return {
      episode: firstEpisode,
      season: null,
      selectionReason: firstEpisode ? 'first-episode' : 'no-data',
      resumptionData: null,
    };
  }

  /**
   * Get debug information about resumption data for a series
   * @param {string} seriesId - The series ID
   * @returns {Object} Debug information about resumption data
   */
  getDebugInfo(seriesId) {
    const continueWatching = userDataService.getContinueWatching();
    const watchAgain = userDataService.getWatchAgain();

    // Use the new method from UserDataService for filtering
    const resumptionData = userDataService.getResumptionDataForSeries(seriesId);
    const seriesContinueWatching = resumptionData?.['continue-watching'] || [];
    const seriesWatchAgain = resumptionData?.['watch-again'] || [];

    return {
      totalContinueWatching: continueWatching.length,
      totalWatchAgain: watchAgain.length,
      seriesContinueWatching: seriesContinueWatching.length,
      seriesWatchAgain: seriesWatchAgain.length,
      seriesContinueWatchingItems: seriesContinueWatching.map(item => ({
        id: item._id,
        title: item.title,
        updatedAt: item.updatedAt,
        point: item.point,
      })),
      seriesWatchAgainItems: seriesWatchAgain.map(item => ({
        id: item._id,
        title: item.title,
        updatedAt: item.updatedAt,
        point: item.point,
      })),
    };
  }

  /**
   * Find which season contains a specific episode
   * @param {Object} episode - The episode to find
   * @param {Array} availableSeasons - All available seasons
   * @returns {Object|null} The season containing the episode, or null if not found
   */
  findSeasonForEpisode(episode, availableSeasons) {
    if (!episode || !availableSeasons || availableSeasons.length === 0) {
      return null;
    }

    // Try to match by channel_id first (most reliable)
    if (episode.channel_id) {
      const season = availableSeasons.find(season => season._id === episode.channel_id);
      if (season) {
        return season;
      }
    }

    // Try to match by channel._id
    if (episode.channel?._id) {
      const season = availableSeasons.find(season => season._id === episode.channel._id);
      if (season) {
        return season;
      }
    }

    // Try to match by channel.channel_id
    if (episode.channel?.channel_id) {
      const season = availableSeasons.find(season => season._id === episode.channel.channel_id);
      if (season) {
        return season;
      }
    }

    // Try to match by parent_channel._id (for series-level episodes)
    if (episode.parent_channel?._id) {
      const season = availableSeasons.find(season => season._id === episode.parent_channel._id);
      if (season) {
        return season;
      }
    }

    return null;
  }

  /**
   * Get the next episode to watch after completing an episode
   * This handles cross-season progression intelligently
   * @param {string} seriesId - The series ID
   * @param {string} completedEpisodeId - The ID of the completed episode
   * @param {Array} allSeasons - All available seasons for the series
   * @returns {Object|null} Next episode information with season context
   */
  getNextEpisodeAfterCompletion(seriesId, completedEpisodeId, allSeasons = []) {
    try {
      // Get all episodes from all seasons, sorted by season and episode order
      const allEpisodes = allSeasons
        .flatMap(season =>
          (season.playlist || []).map(episode => ({
            ...episode,
            seasonId: season._id,
            seasonNumber: season.season_number || season.title,
            season: season,
          })),
        )
        .sort((a, b) => {
          // Sort by season first, then by episode order
          const seasonA = parseInt(a.season_number || 1);
          const seasonB = parseInt(b.season_number || 1);
          if (seasonA !== seasonB) {return seasonA - seasonB;}

          const episodeA = parseInt(a.episode_number || a.order || 1);
          const episodeB = parseInt(b.episode_number || b.order || 1);
          return episodeA - episodeB;
        });

      if (allEpisodes.length === 0) {
        return null;
      }

      // Find the completed episode
      const completedEpisodeIndex = allEpisodes.findIndex(ep => ep._id === completedEpisodeId);

      if (completedEpisodeIndex === -1) {
        // Completed episode not found, return first episode
        return {
          episode: allEpisodes[0],
          season: allEpisodes[0].season,
          isNextSeason: false,
          isSeriesComplete: false,
        };
      }

      // Check if there's a next episode
      const nextEpisodeIndex = completedEpisodeIndex + 1;

      if (nextEpisodeIndex < allEpisodes.length) {
        const nextEpisode = allEpisodes[nextEpisodeIndex];
        const completedEpisode = allEpisodes[completedEpisodeIndex];

        // Check if we're moving to a new season
        const isNextSeason = nextEpisode.seasonId !== completedEpisode.seasonId;

        return {
          episode: nextEpisode,
          season: nextEpisode.season,
          isNextSeason: isNextSeason,
          isSeriesComplete: false,
        };
      } else {
        // No more episodes - series is complete
        return {
          episode: null,
          season: null,
          isNextSeason: false,
          isSeriesComplete: true,
        };
      }
    } catch (error) {
      console.error(
        '🎬 EpisodeSelectionService: Error getting next episode after completion:',
        error,
      );
      return null;
    }
  }

  /**
   * Get the appropriate episode for "Continue Watching" based on latest completion
   * @param {string} seriesId - The series ID
   * @param {Array} allSeasons - All available seasons for the series
   * @returns {Object|null} Episode to continue watching with context
   */
  getContinueWatchingEpisode(seriesId, allSeasons = []) {
    try {
      const latestResumption = userDataService.getLatestResumption(seriesId);

      if (!latestResumption) {
        // No resumption data, start from first episode
        const firstSeason = allSeasons[0];
        if (firstSeason && firstSeason.playlist && firstSeason.playlist.length > 0) {
          return {
            episode: firstSeason.playlist[0],
            season: firstSeason,
            reason: 'first-episode',
          };
        }
        return null;
      }

      if (latestResumption.type === 'continue-watching') {
        // User has partial progress on an episode
        const targetSeason = this.findSeasonForEpisode(latestResumption.episode, allSeasons);
        return {
          episode: latestResumption.episode,
          season: targetSeason,
          reason: 'continue-watching',
        };
      } else if (latestResumption.type === 'watch-again') {
        // User completed an episode, find the next one
        const nextInfo = this.getNextEpisodeAfterCompletion(
          seriesId,
          latestResumption.episode._id,
          allSeasons,
        );

        if (nextInfo && nextInfo.episode) {
          return {
            episode: nextInfo.episode,
            season: nextInfo.season,
            reason: 'next-after-completion',
            isNextSeason: nextInfo.isNextSeason,
          };
        } else if (nextInfo && nextInfo.isSeriesComplete) {
          // Series is complete, offer to restart
          const firstSeason = allSeasons[0];
          if (firstSeason && firstSeason.playlist && firstSeason.playlist.length > 0) {
            return {
              episode: firstSeason.playlist[0],
              season: firstSeason,
              reason: 'series-complete-restart',
            };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('🎬 EpisodeSelectionService: Error getting continue watching episode:', error);
      return null;
    }
  }
  /**
   * Get all episodes from all seasons, sorted by season and episode order
   * @param {Array} availableSeasons - Array of all seasons for the series
   * @returns {Array} Array of all episodes sorted by season and episode order
   */
  getAllEpisodesFromAllSeasons(availableSeasons) {
    const allEpisodes = [];

    availableSeasons.forEach(season => {
      if (season.playlist && season.playlist.length > 0) {
        season.playlist.forEach(episode => {
          allEpisodes.push({
            ...episode,
            seasonNumber: parseInt(season.season_number || 1),
            episodeNumber: parseInt(episode.episode_number || episode.order || 1),
            seasonId: season._id,
          });
        });
      }
    });

    // Sort by season first, then by episode order
    allEpisodes.sort((a, b) => {
      if (a.seasonNumber !== b.seasonNumber) {return a.seasonNumber - b.seasonNumber;}
      return a.episodeNumber - b.episodeNumber;
    });

    return allEpisodes;
  }

  /**
   * Check if there are more episodes available after a given episode
   * @param {Object} episode - The episode to check from
   * @param {Array} availableSeasons - Array of all seasons for the series
   * @returns {boolean} True if there are more episodes available
   */
  hasMoreEpisodesAfter(episode, availableSeasons) {
    const allEpisodes = this.getAllEpisodesFromAllSeasons(availableSeasons);

    if (allEpisodes.length === 0) {return false;}

    // Find the episode in the sorted list
    const episodeIndex = allEpisodes.findIndex(ep => ep._id === episode._id);

    // Check if there are more episodes after this one
    return episodeIndex >= 0 && episodeIndex < allEpisodes.length - 1;
  }
}

// Export singleton instance
export const episodeSelectionService = new EpisodeSelectionService();
export default episodeSelectionService;
